'use client'

import { useZoomImageWheel } from '@zoom-image/react'
import React, { useCallback, useEffect, useRef } from 'react'

export type ZoomImage = { src: string; alt: string }

type ImageZoomModalProps = {
  isOpen: boolean
  onClose: () => void
  images: ZoomImage[]
  initialIndex?: number
}

export function ImageZoomModal({
  isOpen,
  onClose,
  images,
  initialIndex = 0,
}: ImageZoomModalProps) {
  const [index, setIndex] = React.useState(initialIndex)
  const overlayRef = useRef<HTMLDivElement>(null)
  const zoomContainerRef = useRef<HTMLDivElement>(null)
  const { createZoomImage } = useZoomImageWheel()

  const current = images[index] ?? images[0]
  const hasMultiple = images.length > 1
  const canPrev = hasMultiple && index > 0
  const canNext = hasMultiple && index < images.length - 1

  const goPrev = useCallback(() => {
    if (canPrev) setIndex((i) => i - 1)
  }, [canPrev])

  const goNext = useCallback(() => {
    if (canNext) setIndex((i) => i + 1)
  }, [canNext])

  useEffect(() => {
    if (!isOpen) return
    setIndex(Math.min(initialIndex, Math.max(images.length - 1, 0)))
  }, [isOpen, initialIndex, images.length])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft') goPrev()
      if (e.key === 'ArrowRight') goNext()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose, goPrev, goNext])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !zoomContainerRef.current || !current) return
    createZoomImage(zoomContainerRef.current, {
      maxZoom: 4,
      wheelZoomRatio: 0.1,
      shouldZoomOnSingleTouch: () => true,
    })
  }, [isOpen, index, current?.src, createZoomImage, current])

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose()
  }

  if (!isOpen) return null

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label="Image zoom"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4"
      onClick={handleOverlayClick}
    >
      {hasMultiple && canPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            goPrev()
          }}
          className="absolute left-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:left-4"
          aria-label="Previous image"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}

      {hasMultiple && canNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            goNext()
          }}
          className="absolute right-2 top-1/2 z-10 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white md:right-4"
          aria-label="Next image"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}

      <div
        className="relative flex max-h-[85vh] max-w-[90vw] items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-0 top-0 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          aria-label="Close zoom"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
        {current && (
          <div
            ref={zoomContainerRef}
            className="relative max-h-[85vh] max-w-[90vw] overflow-hidden touch-none"
            style={{ touchAction: 'none' }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              key={current.src}
              src={current.src}
              alt={current.alt}
              className="max-h-[85vh] w-auto max-w-[90vw] object-contain"
              draggable={false}
            />
          </div>
        )}
      </div>

      {hasMultiple && (
        <span className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-white/10 px-3 py-1 font-poppins text-sm text-white">
          {index + 1} / {images.length}
        </span>
      )}
    </div>
  )
}
